import React, { useEffect } from "react";
import { twMerge } from "tailwind-merge";

import CloseIcon from "@/components/App/close.svg?react";

type ModalProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showCloseButton?: boolean;
};

const Modal = (props: ModalProps) => {
  const { open, onClose, children, showCloseButton = true } = props;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      // Lock body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // Restore body scroll when modal closes or component unmounts
      document.body.style.overflow = "unset";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={twMerge(
        "z-[1000] fixed inset-0 h-screen w-screen bg-slate-900 bg-opacity-40",
      )}
      onClick={handleOverlayClick}
    >
      <div
        className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <CloseButton
            onClose={onClose}
            className={"absolute right-2 top-2 z-10"}
          />
        )}
        {children}
      </div>
    </div>
  );
};

type CloseButtonProps = {
  onClose: () => void;
} & React.ComponentPropsWithoutRef<"button">;

function CloseButton(props: CloseButtonProps) {
  const { onClose, ...rest } = props;
  return (
    <button
      className="p-8 text-amalfi-coast hover:text-studer-blue focus:outline-amalfi-coast"
      onClick={onClose}
      aria-expanded="true"
      {...rest}
    >
      <CloseIcon width="14" aria-hidden />
    </button>
  );
}

export default Modal;
